import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  DeleteOutline as DeleteOutlineIcon,
  FormatQuote as FormatQuoteIcon,
  Groups as GroupsIcon,
  HowToReg as HowToRegIcon,
  ListAlt as ListAltIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CandidatesTable from '../components/Candidates/CandidatesTable';
import CycleWeightsCard from '../components/Cycles/CycleWeightsCard';
import BankCloneModal, { BankItem } from '../components/Cycles/BankCloneModal';
import CombinedGearCard from '../components/Cycles/CombinedGearCard';
import CycleStatementsTab from '../components/Cycles/CycleStatementsTab';
import RegistrationTab from '../components/Cycles/RegistrationTab';
import ErrorAlert from '../components/Common/ErrorAlert';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import InterviewersTable from '../components/Users/InterviewersTable';
import TeamAssignmentsTable from '../components/Users/TeamAssignmentsTable';
import { useDirection } from '../context/DirectionContext';
import { translate as tr, translateOr } from '../i18n/translations';
import apiService from '../services/api';
import { CycleFormAssignment, CycleStatus, FormTemplate, Station } from '../types/api';

// ── Helpers ────────────────────────────────────────────────────────────────

const statusConfig = (status: CycleStatus) => {
  if (status === CycleStatus.ACTIVE) return { color: 'success' as const, dot: '#10B981' };
  if (status === CycleStatus.DRAFT) return { color: 'warning' as const, dot: '#F59E0B' };
  return { color: 'default' as const, dot: '#94A3B8' };
};


// ── Forms tab ──────────────────────────────────────────────────────────────

const FormsTab: React.FC<{ cycleId: string; cycleStatus: CycleStatus }> = ({ cycleId, cycleStatus }) => {
  const qc = useQueryClient();
  const { lang } = useDirection();
  const [bankOpen, setBankOpen] = React.useState(false);
  const [editWeights, setEditWeights] = React.useState<Record<string, string>>({});

  const formTypeLabel: Record<string, { label: string; color: 'primary' | 'success' | 'info' | 'warning' }> = {
    generic:      { label: tr('formType.generic', lang),      color: 'primary' },
    socio:        { label: tr('formType.socio', lang),        color: 'info' },
    teamleadRank: { label: tr('formType.teamleadRank', lang), color: 'warning' },
    interview:    { label: tr('formType.interview', lang),    color: 'success' },
  };

  const { data: assignments, isLoading, error } = useQuery({
    queryKey: ['formAssignments', cycleId],
    queryFn: () => apiService.getFormAssignmentsByCycle(cycleId),
    enabled: !!cycleId,
  });

  const { data: bankTemplates = [] } = useQuery({
    queryKey: ['formTemplates'],
    queryFn: () => apiService.getFormTemplates(),
    enabled: bankOpen,
  });

  // Sync editWeights when data loads
  React.useEffect(() => {
    if (assignments) {
      setEditWeights(prev => {
        const next: Record<string, string> = {};
        assignments.forEach((a: CycleFormAssignment) => {
          next[a.id] = prev[a.id] ?? String(a.weightInFinalScore);
        });
        return next;
      });
    }
  }, [assignments]);

  const addMutation = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map(templateId =>
        apiService.createFormAssignment({ templateId, cycleId, weightInFinalScore: 1.0 })
      )),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['formAssignments', cycleId] }); setBankOpen(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteFormAssignment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['formAssignments', cycleId] }),
  });

  const weightMutation = useMutation({
    mutationFn: ({ id, weight }: { id: string; weight: number }) =>
      apiService.updateFormAssignment(id, { weightInFinalScore: weight }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['formAssignments', cycleId] }),
  });

  if (isLoading) return <LoadingSpinner message={tr('form.loadingForms', lang)} />;
  if (error) return <ErrorAlert error={error} title={tr('form.errorLoad', lang)} />;

  const totalWeight = (assignments ?? []).reduce(
    (sum: number, a: CycleFormAssignment) => sum + Number(a.weightInFinalScore), 0
  );
  const weightOk = totalWeight <= 1.0 + 1e-9;

  const bankItems: BankItem[] = (bankTemplates as FormTemplate[]).map(t => ({
    id: t.id,
    name: t.name,
    subtitle: formTypeLabel[t.formType]?.label ?? t.formType,
  }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {assignments?.length ? (
          <Alert
            severity={weightOk ? 'success' : 'warning'}
            sx={{ py: 0.5, flex: 1, mr: 2, fontSize: '0.8rem' }}
          >
            {tr('form.combinedWeight', lang)} <strong>{totalWeight.toFixed(2)}</strong> / 1.00
            {!weightOk && ` — ${tr('form.exceedsMax', lang)}`}
          </Alert>
        ) : <Box />}
        <Button variant="outlined" startIcon={<AddIcon />} size="small" onClick={() => setBankOpen(true)}>
          {tr('form.addFromBank', lang)}
        </Button>
      </Box>

      {!assignments?.length ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
          <Typography>{tr('form.noneConfigured', lang)}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
          {(assignments as CycleFormAssignment[]).map(assignment => {
            const ft = formTypeLabel[assignment.formType] ?? { label: assignment.formType, color: 'default' as any };
            const localWeight = editWeights[assignment.id] ?? String(assignment.weightInFinalScore);
            return (
              <Box
                key={assignment.id}
                sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.06)', position: 'relative' }}
              >
                <Tooltip title={tr('form.removeFromCycle', lang)}>
                  <IconButton
                    size="small"
                    onClick={() => deleteMutation.mutate(assignment.id)}
                    disabled={deleteMutation.isPending}
                    sx={{ position: 'absolute', top: 8, right: 8, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, pr: 3 }}>
                  <Chip label={ft.label} color={ft.color} size="small" />
                  {!assignment.isActive && (
                    <Chip label={tr('form.inactive', lang)} size="small" sx={{ bgcolor: '#F1F5F9', color: '#94A3B8' }} />
                  )}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A', mb: 1.5 }}>
                  {assignment.template.name}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.775rem', color: '#64748B' }}>{tr('form.weightInFinal', lang)}</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={localWeight}
                      onChange={e => setEditWeights(w => ({ ...w, [assignment.id]: e.target.value }))}
                      onBlur={() => {
                        const v = parseFloat(localWeight);
                        if (!isNaN(v) && v >= 0 && v <= 1) {
                          weightMutation.mutate({ id: assignment.id, weight: v });
                        } else {
                          setEditWeights(w => ({ ...w, [assignment.id]: String(assignment.weightInFinalScore) }));
                        }
                      }}
                      inputProps={{ min: 0, max: 1, step: 0.05 }}
                      sx={{ width: 80 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '0.775rem', color: '#64748B' }}>{tr('form.mandatory', lang)}</Typography>
                    <Chip
                      label={assignment.isMandatoryForClosure ? tr('common.yes', lang) : tr('common.no', lang)}
                      size="small"
                      color={assignment.isMandatoryForClosure ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                  {assignment.template.roleRelevancy?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: '0.775rem', color: '#64748B', mb: 0.5 }}>{tr('common.roles', lang)}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {assignment.template.roleRelevancy.map((r: string) => (
                          <Chip key={r} label={translateOr(`role.${r}`, lang, r)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <BankCloneModal
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        title={tr('form.addFromBankTitle', lang)}
        items={bankItems}
        onConfirm={ids => addMutation.mutate(ids)}
        loading={addMutation.isPending}
      />

      <CycleWeightsCard cycleId={cycleId} cycleStatus={cycleStatus} />
    </>
  );
};

// ── Stations tab ───────────────────────────────────────────────────────────

const StationsTab: React.FC<{ cycleId: string }> = ({ cycleId }) => {
  const qc = useQueryClient();
  const { lang } = useDirection();
  const [bankOpen, setBankOpen] = React.useState(false);

  const stationTypeLabel: Record<string, { label: string; color: 'primary' | 'success' | 'info' | 'warning' | 'default' }> = {
    ordered:      { label: tr('stationType.ordered', lang),      color: 'primary' },
    accumulative: { label: tr('stationType.accumulative', lang), color: 'success' },
    individual:   { label: tr('stationType.individual', lang),   color: 'info' },
  };

  const { data: stations, isLoading, error } = useQuery({
    queryKey: ['stations', cycleId],
    queryFn: () => apiService.getStationsByCycle(cycleId),
    enabled: !!cycleId,
  });

  const { data: bankStations = [] } = useQuery({
    queryKey: ['bankStations'],
    queryFn: () => apiService.getBankStations(),
    enabled: bankOpen,
  });

  const cloneMutation = useMutation({
    mutationFn: (ids: string[]) => apiService.cloneStationsIntoCycle(cycleId, ids),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['stations', cycleId] }); setBankOpen(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteStation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stations', cycleId] }),
  });

  if (isLoading) return <LoadingSpinner message={tr('station.loadingStations', lang)} />;
  if (error) return <ErrorAlert error={error} title={tr('station.errorLoad', lang)} />;

  const bankItems: BankItem[] = bankStations.map(s => ({
    id: s.id,
    name: s.name,
    subtitle: stationTypeLabel[s.stationType]?.label ?? s.stationType,
  }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => setBankOpen(true)}
        >
          {tr('station.addFromBank', lang)}
        </Button>
      </Box>

      {!!stations?.length && <CombinedGearCard stations={stations} />}

      {!stations?.length ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
          <Typography>{tr('station.noneConfigured', lang)}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
          {stations.map((station: Station) => {
            const st = stationTypeLabel[station.stationType] ?? { label: station.stationType, color: 'default' as any };
            return (
              <Box
                key={station.id}
                sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.06)', position: 'relative' }}
              >
                <Tooltip title={tr('station.removeFromCycle', lang)}>
                  <IconButton
                    size="small"
                    onClick={() => deleteMutation.mutate(station.id)}
                    disabled={deleteMutation.isPending}
                    sx={{ position: 'absolute', top: 8, right: 8, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, pr: 3 }}>
                  <Chip label={st.label} color={st.color} size="small" />
                  <Chip
                    label={`Level ${station.stationLevel}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A', mb: 1 }}>
                  {station.name}
                </Typography>
                {station.description && (
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 1 }}>
                    {station.description}
                  </Typography>
                )}
                {station.legacyStationId && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.775rem', color: '#64748B' }}>{tr('station.legacyId', lang)}</Typography>
                    <Typography sx={{ fontSize: '0.775rem', fontWeight: 600, color: '#0F172A' }}>
                      {station.legacyStationId}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <BankCloneModal
        open={bankOpen}
        onClose={() => setBankOpen(false)}
        title={tr('station.addFromBankTitle', lang)}
        items={bankItems}
        onConfirm={ids => cloneMutation.mutate(ids)}
        loading={cloneMutation.isPending}
      />
    </>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const CycleDetail: React.FC = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const { lang } = useDirection();
  const [tab, setTab] = useState(0);

  const { data: cycle, isLoading, error } = useQuery({
    queryKey: ['cycle', cycleId],
    queryFn: () => (cycleId ? apiService.getCycle(cycleId) : Promise.resolve(null)),
    enabled: !!cycleId,
  });

  const { data: cycleStatus } = useQuery({
    queryKey: ['cycleStatus', cycleId],
    queryFn: () => apiService.getCycleStatus(cycleId!),
    enabled: !!cycleId,
  });

  if (isLoading) return <LoadingSpinner message={tr('cd.loading', lang)} />;
  if (error || !cycle)
    return <ErrorAlert error={error || new Error(tr('cd.notFound', lang))} title={tr('cd.errorLoad', lang)} />;

  const sc = statusConfig(cycle.status);

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tooltip title={tr('cd.backTooltip', lang)} placement="right">
            <IconButton
              size="small"
              onClick={() => navigate('/cycles')}
              sx={{ color: '#64748B', '&:hover': { color: '#0F172A', bgcolor: '#F1F5F9' } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            component="button"
            onClick={() => navigate('/cycles')}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748B',
              fontSize: '0.85rem',
              p: 0,
              '&:hover': { color: '#0F172A' },
            }}
          >
            {tr('nav.cycles', lang)}
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontSize: '0.85rem' }}>/</Typography>
          <Typography sx={{ color: '#0F172A', fontSize: '0.85rem', fontWeight: 600 }}>
            {cycle.displayName}
          </Typography>
        </Box>

        {/* Cycle title + meta bar */}
        <Box
          sx={{
            bgcolor: 'white',
            border: '1px solid #E2E8F0',
            borderRadius: 3,
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: sc.dot,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                {cycle.displayName}
              </Typography>
              <Typography sx={{ color: '#64748B', fontSize: '0.8rem', mt: 0.25 }}>
                {cycle.monthYear}
              </Typography>
            </Box>
            <Chip label={translateOr(`cycleStatus.${cycle.status}`, lang, cycle.status)} color={sc.color} size="small" sx={{ ml: 0.5 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {cycle.startedAt && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  {tr('cd.started', lang)}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A' }}>
                  {new Date(cycle.startedAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            {cycle.endedAt && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  {tr('cd.ended', lang)}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A' }}>
                  {new Date(cycle.endedAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                {tr('cd.created', lang)}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0F172A' }}>
                {new Date(cycle.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Aggregate Status Strip */}
      {cycleStatus?.aggregate && (() => {
        const agg = cycleStatus.aggregate!;
        const metrics = [
          { label: tr('cd.agg.total', lang), value: agg.total, color: '#64748B', bg: '#F8FAFC' },
          { label: tr('cd.agg.active', lang), value: agg.active, color: '#10B981', bg: '#F0FDF4' },
          { label: tr('cd.agg.inactive', lang), value: agg.inactive, color: '#EF4444', bg: '#FEF2F2' },
          { label: tr('cd.agg.completed', lang), value: agg.gibushCompleted, color: '#6366F1', bg: '#EEF2FF' },
          { label: tr('cd.agg.incompleted', lang), value: agg.gibushIncompleted, color: '#F59E0B', bg: '#FFFBEB' },
          { label: tr('cd.agg.unknown', lang), value: agg.unknown, color: '#94A3B8', bg: '#F1F5F9' },
        ];
        return (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
            {metrics.map(m => (
              <Box key={m.label} sx={{ bgcolor: m.bg, border: `1px solid ${m.color}22`, borderRadius: 2, px: 2, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.value}</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: m.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.25 }}>{m.label}</Typography>
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* סיכום כללי — Matam breakdown */}
      {cycleStatus?.teams && (() => {
        const allCandidates = cycleStatus.teams.flatMap((t: any) => t.candidates ?? []);
        // Only candidates that have a matam assigned
        const withMatam = allCandidates.filter((c: any) => c.candidatePersonalMatam != null);
        if (withMatam.length === 0) return null;

        // Group by matam value: { [matam]: { active, total } }
        const breakdown = new Map<number, { active: number; total: number }>();
        for (const c of withMatam) {
          const m = c.candidatePersonalMatam as number;
          const entry = breakdown.get(m) ?? { active: 0, total: 0 };
          entry.total += 1;
          if (c.status === 'active') entry.active += 1;
          breakdown.set(m, entry);
        }
        const sorted = Array.from(breakdown.entries()).sort(([a], [b]) => a - b);

        return (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
              סיכום כללי
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {sorted.map(([matam, { active, total }]) => {
                const pct = total > 0 ? Math.round((active / total) * 100) : 0;
                const barColor = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444';
                return (
                  <Box
                    key={matam}
                    sx={{
                      bgcolor: 'white',
                      border: '1px solid #E2E8F0',
                      borderRadius: 2,
                      px: 2,
                      py: 1.25,
                      minWidth: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        מתא״ם {matam}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>
                        {pct}%
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#0F172A', lineHeight: 1 }}>
                      {active}
                      <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#94A3B8' }}>
                        {' '}/ {total}
                      </Typography>
                    </Typography>
                    {/* Progress bar */}
                    <Box sx={{ height: 3, bgcolor: '#F1F5F9', borderRadius: 2, overflow: 'hidden', mt: 0.25 }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: barColor, borderRadius: 2, transition: 'width 0.4s ease' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #E2E8F0', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<ListAltIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.candidates', lang)} />
          <Tab icon={<GroupsIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.team', lang)} />
          <Tab icon={<RecordVoiceOverIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.interviews', lang)} />
          <Tab icon={<AssignmentIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.forms', lang)} />
          <Tab icon={<AccountTreeIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.stations', lang)} />
          <Tab icon={<FormatQuoteIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.statements', lang)} />
          <Tab icon={<HowToRegIcon fontSize="small" />} iconPosition="start" label={tr('cd.tab.registration', lang)} />
        </Tabs>
      </Box>

      {tab === 0 && <CandidatesTable cycleId={cycleId!} />}
      {tab === 1 && <TeamAssignmentsTable cycleId={cycleId!} />}
      {tab === 2 && <InterviewersTable cycleId={cycleId!} />}
      {tab === 3 && <FormsTab cycleId={cycleId!} cycleStatus={cycle.status} />}
      {tab === 4 && <StationsTab cycleId={cycleId!} />}
      {tab === 5 && <CycleStatementsTab cycleId={cycleId!} locked={cycle.status !== CycleStatus.DRAFT} />}
      {tab === 6 && <RegistrationTab cycleId={cycleId!} cycleStatus={cycle.status} />}
    </motion.div>
  );
};

export default CycleDetail;
